import { useState, useEffect } from "react";
import { useCookieConsent, CookieChoices } from "@/contexts/CookieConsentContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Link } from "react-router-dom";

export const CookiePreferencesModal = () => {
  const { showModal, closeModal, savePreferences, consent } = useCookieConsent();
  const [choices, setChoices] = useState<CookieChoices>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    if (consent) {
      setChoices(consent.choices);
    }
  }, [consent]);

  const handleSave = () => {
    savePreferences(choices);
  };

  return (
    <Dialog open={showModal} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="cookie-preferences-description">
        <DialogHeader>
          <DialogTitle id="cookie-preferences-title">Preferenze cookie</DialogTitle>
          <DialogDescription id="cookie-preferences-description">
            Gestisci le tue preferenze sui cookie. Puoi modificarle in qualsiasi momento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="necessary" className="text-base font-semibold">
                  Cookie necessari
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Sempre attivi per il corretto funzionamento del sito. Non possono essere disattivati.
                </p>
              </div>
              <Switch
                id="necessary"
                checked={choices.necessary}
                disabled
                aria-label="Cookie necessari sempre attivi"
              />
            </div>
          </div>

          <Separator />

          {/* Analytics */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  Cookie analitici
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Per capire come usi il sito e migliorarlo. Include Google Analytics e Hotjar.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={choices.analytics}
                onCheckedChange={(checked) => setChoices({ ...choices, analytics: checked })}
                aria-label="Attiva o disattiva cookie analitici"
              />
            </div>
          </div>

          <Separator />

          {/* Marketing */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="marketing" className="text-base font-semibold">
                  Cookie marketing
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Per misurare campagne pubblicitarie e mostrarti contenuti personalizzati. Include Meta Pixel e Google Ads.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={choices.marketing}
                onCheckedChange={(checked) => setChoices({ ...choices, marketing: checked })}
                aria-label="Attiva o disattiva cookie marketing"
              />
            </div>
          </div>

          <Separator />

          {/* Functional */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="functional" className="text-base font-semibold">
                  Cookie funzionali
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Migliorano l'esperienza utente con funzionalità aggiuntive come video e chat.
                </p>
              </div>
              <Switch
                id="functional"
                checked={choices.functional}
                onCheckedChange={(checked) => setChoices({ ...choices, functional: checked })}
                aria-label="Attiva o disattiva cookie funzionali"
              />
            </div>
          </div>
        </div>

        <Separator />

        <div className="space-y-3 text-xs text-muted-foreground">
          <p>
            Versione policy: 1.0.0 – Novembre 2025
          </p>
          <p>
            Per maggiori informazioni consulta la nostra{" "}
            <Link to="/privacy-policy" className="underline hover:text-foreground">
              Privacy Policy
            </Link>{" "}
            e la{" "}
            <Link to="/cookie-policy" className="underline hover:text-foreground">
              Cookie Policy
            </Link>
            .
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={closeModal}>
            Annulla
          </Button>
          <Button onClick={handleSave}>
            Salva preferenze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
